import { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';

/**
 * CircularProgress — minimal cyan ring with animated fill.
 */
export default function CircularProgress({
  value = 0,           // 0..100
  size = 120,
  stroke = 4,
  label = '',
  sublabel = '',
  accent = '#90E0EF',
  track = '#1a1a1a',
  duration = 1.4,
  delay = 0,
  testId,
}) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = useMotionValue(0);
  const dash = useTransform(progress, (v) => `${(v / 100) * circumference} ${circumference}`);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(progress, Math.max(0, Math.min(100, value || 0)), {
      duration,
      delay,
      ease: 'easeOut',
      onUpdate: (latest) => setDisplay(Math.round(latest)),
    });
    return () => controls.stop();
  }, [value, duration, delay, progress]);

  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{ width: size }}
      data-testid={testId}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={track}
            strokeWidth={stroke}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={accent}
            strokeWidth={stroke}
            strokeLinecap="round"
            style={{
              strokeDasharray: dash,
              filter: `drop-shadow(0 0 6px ${accent}66)`,
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-outfit text-2xl font-semibold text-white tabular-nums">
            {display}
            <span className="text-sm text-[#555] ml-0.5">%</span>
          </span>
          {sublabel && (
            <span className="text-[9px] font-mono text-[#555] tracking-wider uppercase mt-0.5">
              {sublabel}
            </span>
          )}
        </div>
      </div>
      {label && (
        <p className="text-[10px] font-mono text-[#888] tracking-[0.2em] uppercase mt-3 text-center">
          {label}
        </p>
      )}
    </div>
  );
}
